// Dashboard at / (architecture sprint-04 §6.1; sprint 7 §7.1): running hero, quick-start, Quick actions,
// summary cards, today's timeline, recent entries and this week by project. One EntryFormDialog for Log
// time and edits.
//
// Sprint 15 (architecture sprint-15 §9.3): the Dashboard starts with a project selector. Activity wins: an open
// shift shows the Shift card (plus "Add note" under it), a timer shows the hero and Log time; otherwise a shift
// project shows Start Shift for that project only, a standard project the quick-start and Quick actions for it.
// Members of shift-only projects therefore never see timer or log quick actions.
import { addDays, monthRange, weekRange, type LocalDate } from '@shared/calendar';
import { NotebookPen } from 'lucide-react';
import { useState } from 'react';
import { SelfNoteDialog } from '@/components/notes/SelfNoteDialog';
import { Button } from '@/components/ui/button';
import { useOwnNoteProjects } from '@/hooks/use-own-notes';
import { useSelectedProject } from '@/hooks/use-selected-project';
import { useStartOptions } from '@/hooks/use-shifts';
import { EntryFormDialog } from '@/components/entries/EntryFormDialog';
import { Page } from '@/components/Page';
import { PageHeader } from '@/components/PageHeader';
import { ShiftCard } from '@/components/shifts/ShiftCard';
import { HeroTimer } from '@/components/timer/HeroTimer';
import { Skeleton } from '@/components/ui/skeleton';
import { useQuickStart, useRangeEntries } from '@/hooks/use-entries';
import { useMe } from '@/hooks/use-me';
import { useActiveShift } from '@/hooks/use-shifts';
import { useDailyTotals } from '@/hooks/use-reports';
import { useTimer } from '@/hooks/use-timer';
import { dashboardTotals, weekByProject } from '@/lib/dashboard-model';
import { useToday } from '@/lib/use-now';
import type { EntryListItem } from '@/types/entries';
import type { Me } from '@/types/me';
import { DashboardProjectSelector } from './DashboardProjectSelector';
import { QuickActions } from './QuickActions';
import { QuickStartCard } from './QuickStartCard';
import { RecentEntries } from './RecentEntries';
import { SummaryCards } from './SummaryCards';
import { TodayTimeline } from './TodayTimeline';
import { WeekByProject } from './WeekByProject';

export const QUICK_START_DISMISS_KEY = 'tt-quickstart-dismissed';
const RECENT_DAYS = 7;
export const NO_SHIFT_TO_START = 'No shift to start on this project right now.';
export const CHOOSE_PROJECT = 'Choose a project to start.';
export const ADD_NOTE = 'Add note';

/** Sprint 15 (A-10): the on-shift "Add note" row under the Shift card, when notes are on for the shift's project. */
function ShiftNoteAction({ projectId, shiftId, tz }: { projectId: string; shiftId: string; tz: string }): JSX.Element | null {
  const projects = useOwnNoteProjects();
  const [open, setOpen] = useState(false);
  if (!(projects.data ?? []).some((p) => p.id === projectId)) return null;
  return (
    <div className="-mt-4 mb-8 flex justify-end">
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <NotebookPen aria-hidden="true" />
        {ADD_NOTE}
      </Button>
      <SelfNoteDialog open={open} onOpenChange={setOpen} timezone={tz} initialProjectId={projectId} initialShiftId={shiftId} />
    </div>
  );
}

type FormTarget = { mode: 'create' } | { mode: 'edit'; entry: EntryListItem } | null;

function dismissValue(userId: string, today: LocalDate): string {
  return `${userId}|${today}`;
}

function readDismissed(userId: string, today: LocalDate): boolean {
  try {
    return window.localStorage.getItem(QUICK_START_DISMISS_KEY) === dismissValue(userId, today);
  } catch {
    return false;
  }
}

function minDate(a: LocalDate, b: LocalDate): LocalDate {
  return a < b ? a : b;
}

function maxDate(a: LocalDate, b: LocalDate): LocalDate {
  return a > b ? a : b;
}

export function DashboardPage(): JSX.Element {
  const { data: me } = useMe();
  if (!me)
    return (
      <Page width="5xl">
        <Skeleton className="h-96 w-full" />
      </Page>
    );
  return <Dashboard me={me} />;
}

function Dashboard({ me }: { me: Me }): JSX.Element {
  const tz = me.timezone;
  const today = useToday(tz);
  const week = weekRange(today);
  const month = monthRange(today);
  const [form, setForm] = useState<FormTarget>(null);
  const [dismissedFor, setDismissedFor] = useState<string | null>(null);
  const dismissed = dismissedFor === dismissValue(me.id, today) || readDismissed(me.id, today);

  const timer = useTimer();
  const recent = useRangeEntries(addDays(today, 1 - RECENT_DAYS), today);
  // One call (≤ 37 days) feeds the summary cards and this week by project.
  const totals = useDailyTotals(minDate(week.start, month.start), maxDate(week.end, month.end), undefined);
  // Sprint 13 (§8): no quick-start while on shift (starting a timer is refused then).
  const openShift = useActiveShift()?.shift ?? null;
  const onShift = openShift !== null;
  const selected = useSelectedProject();
  const selection = selected.selection;
  const startOptions = useStartOptions();
  const showsStandard = !onShift && !timer.data && selection?.kind === 'standard';
  const quickEnabled = !timer.isLoading && !timer.data && !dismissed && !onShift && (selection === null || showsStandard);
  const quick = useQuickStart(quickEnabled);
  const suggestion = quickEnabled ? quick.data?.suggestion : null;

  function dismiss(): void {
    const value = dismissValue(me.id, today);
    setDismissedFor(value);
    try {
      window.localStorage.setItem(QUICK_START_DISMISS_KEY, value);
    } catch {
      // Storage blocked: dismissed for this page view only.
    }
  }

  const logTime = (): void => setForm({ mode: 'create' });
  const timerProjectId = timer.data?.timer.project?.id ?? null;
  const logProjectId = timer.data ? timerProjectId : showsStandard ? (selection?.project?.id ?? null) : null;
  const hasStandard = selected.candidates.some((c) => c.kind === 'standard');

  let cards: JSX.Element;
  if (openShift) {
    cards = (
      <>
        <ShiftCard tz={tz} />
        <ShiftNoteAction projectId={openShift.project.id} shiftId={openShift.id} tz={tz} />
      </>
    );
  } else if (timer.data) {
    cards = <QuickActions onLogTime={logTime} projectId={timerProjectId} />;
  } else if (selection?.kind === 'shift' && selection.project) {
    const hasOption = (startOptions.data ?? []).some((o) => o.project.id === selection.project?.id);
    cards =
      hasOption || startOptions.isLoading ? (
        <ShiftCard tz={tz} projectId={selection.project.id} />
      ) : (
        <p className="mb-8 text-sm text-muted-foreground">{NO_SHIFT_TO_START}</p>
      );
  } else if (selection?.kind === 'standard') {
    cards = (
      <>
        {suggestion && <QuickStartCard suggestion={suggestion} today={today} onDismiss={dismiss} />}
        <QuickActions onLogTime={logTime} projectId={selection.project?.id ?? null} />
      </>
    );
  } else if (!selected.ready || hasStandard || selected.candidates.length === 0) {
    cards = (
      <>
        {suggestion && <QuickStartCard suggestion={suggestion} today={today} onDismiss={dismiss} />}
        <QuickActions onLogTime={logTime} />
      </>
    );
  } else {
    cards = <p className="mb-8 text-sm text-muted-foreground">{CHOOSE_PROJECT}</p>;
  }
  const items = recent.data ?? [];
  const rows = totals.data?.rows ?? [];
  return (
    <Page width="5xl">
      <PageHeader
        title="Dashboard"
        subtitle="Track time and review where it goes."
        hint="An overview of your time: quick actions, running totals, recent entries, and this week's split by project."
      />
      <DashboardProjectSelector state={selected} />
      <HeroTimer />
      {cards}
      <SummaryCards totals={totals.data ? dashboardTotals(rows, today) : undefined} />
      <TodayTimeline entries={items} snapshot={timer.data ?? null} today={today} tz={tz} onEdit={(entry) => setForm({ mode: 'edit', entry })} />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <RecentEntries items={items} onEdit={(entry) => setForm({ mode: 'edit', entry })} {...(selection?.kind === 'shift' ? {} : { onLogTime: logTime })} />
        <WeekByProject items={weekByProject(rows, week, totals.data?.projects ?? [])} timerRunning={Boolean(timer.data)} />
      </div>
      <EntryFormDialog
        open={form !== null}
        onOpenChange={(open) => {
          if (!open) setForm(null);
        }}
        mode={form?.mode ?? 'create'}
        entry={form?.mode === 'edit' ? form.entry : undefined}
        initialDate={today}
        {...(form?.mode === 'create' && logProjectId ? { contextProjectId: logProjectId } : {})}
      />
    </Page>
  );
}
